import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { DivAttrs } from '../../utils/HtmlAttrs';
import { ColorMap } from '../AlertStyles';
import { InlineAlert, InlineAlertBaseProps, InlineAlertType, MessageClass } from './InlineAlertBase';

export { InlineAlertType };

export interface CompactInlineAlertProps extends Pick<InlineAlertBaseProps, 'testId' | 'type'> {
  /**
  * Optional icon to pass in to the alert.
  * The icon should be 14px square ⁠— if not, it may need to be repositioned using `iconContainerStyle`.
  * Make sure to include an `alt` attribute if using an `<img/>` as your icon or `aria-label` if not.
  */
  icon?: React.ReactNode;

  /**
  * You may pass in style properties to override the icon container's default styling, if needed.
  * This should very rarely, if ever, be needed.
  */
  iconContainerStyle?: React.CSSProperties;

  /** Setting an `onClick` handler will also cause the `CompactInlineAlert` to show hover feedback. */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

export const InteractiveAlertClass: string = 'interactiveAlert';
export const InteractiveIconClass: string = 'interactiveIconContainer';

export const CompactAlertStyleName: string = TypeStyle.style({
  minHeight: 24,
  $nest: {
    [`& .${MessageClass}`]: {
      flexGrow: 1,
      margin: `3px 8px`,
    },
    [`& .${InteractiveIconClass}`]: {
      flexShrink: 0,
      margin: `6px 8px 0px 8px`,
    },
    [`&.${InteractiveAlertClass}`]: {
      cursor: 'pointer',
      $nest: {
        [`&.${InlineAlertType.Success}:hover`]: {
          backgroundColor: ColorMap.success.hoverColor,
        },
        [`&.${InlineAlertType.Info}:hover`]: {
          backgroundColor: ColorMap.info.hoverColor,
        },
        [`&.${InlineAlertType.Warning}:hover`]: {
          backgroundColor: ColorMap.warning.hoverColor,
        },
        [`&.${InlineAlertType.Error}:hover`]: {
          backgroundColor: ColorMap.error.hoverColor,
        },
      },
    },
  },
});

export const CompactInlineAlertWidget = React.forwardRef<HTMLDivElement, CompactInlineAlertProps & DivAttrs>((props, ref) => {
  const { className, icon, iconContainerStyle, ...restProps } = props;
  const InteractiveIcon = (interactiveIcon: React.ReactNode) =>
    <div
      children={interactiveIcon}
      className={InteractiveIconClass}
      style={iconContainerStyle}
    />;

  return (
    <InlineAlert
      {...restProps}
      className={TypeStyle.classes(className, CompactAlertStyleName, restProps.onClick && InteractiveAlertClass)}
      interactiveElement={icon && InteractiveIcon(icon)}
      ref={ref}
    />
  );
});
CompactInlineAlertWidget.displayName = 'CompactInlineAlertWidget';
